import { useEffect, useState } from "react";

import { GrantConfirm } from "../components/GrantConfirm";
import { Note } from "../components/Note";
import { t } from "../i18n/strings";
import { useGrant } from "../hooks/useGrant";

/**
 * The same ceiling adminwebapi enforces (GRANT_MAX_TOKENS).
 *
 * A courtesy so an admin sees the range before sending, not a control: the point of the
 * ceiling is a leaked session token, and a leaked token does not go through this form.
 */
export const GRANT_MAX_TOKENS = 100000;

interface GrantsScreenProps {
  /** Set when an admin came here from a ticket, to credit that client. */
  userId?: number;
}

const OUTCOME_KINDS = {
  done: "success",
  not_notified: "warning",
  replayed: "info",
} as const;

const OUTCOME_MESSAGES = {
  done: "grants.done",
  not_notified: "grants.notNotified",
  replayed: "grants.replayed",
} as const;

export function GrantsScreen({ userId }: GrantsScreenProps) {
  const grants = useGrant();
  const [user, setUser] = useState(userId ? String(userId) : "");
  const [tokens, setTokens] = useState("");
  const [comment, setComment] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [badInput, setBadInput] = useState<"user" | "tokens" | null>(null);

  useEffect(() => {
    if (userId !== undefined) {
      setUser(String(userId));
    }
  }, [userId]);

  const parsedUser = Number(user.trim());
  const parsedTokens = Number(tokens.trim());

  function open() {
    if (!user.trim() || !Number.isInteger(parsedUser)) {
      setBadInput("user");
      return;
    }
    if (
      !tokens.trim() ||
      !Number.isInteger(parsedTokens) ||
      parsedTokens < 1 ||
      parsedTokens > GRANT_MAX_TOKENS
    ) {
      setBadInput("tokens");
      return;
    }
    setBadInput(null);
    grants.reset();
    setConfirming(true);
  }

  async function confirm() {
    await grants.grant(parsedUser, parsedTokens, comment);
  }

  // the dialog closes only on a finished attempt: a failure keeps it open so the same attempt
  // can be retried, which is what the api's idempotency key is for
  useEffect(() => {
    if (grants.outcome !== null) {
      setConfirming(false);
      setTokens("");
      setComment("");
    }
  }, [grants.outcome]);

  return (
    <section className="screen">
      <h2 className="screen__title">{t("grants.title")}</h2>
      <p className="screen__hint">{t("grants.hint", { max: GRANT_MAX_TOKENS })}</p>

      <div className="lookup">
        <input
          className="lookup__input"
          value={user}
          inputMode="numeric"
          placeholder={t("grants.userPlaceholder")}
          onChange={(event) => setUser(event.target.value)}
        />
        <input
          className="lookup__input"
          value={tokens}
          inputMode="numeric"
          placeholder={t("grants.tokensPlaceholder")}
          onChange={(event) => setTokens(event.target.value)}
        />
      </div>

      <div className="lookup">
        <input
          className="lookup__input"
          value={comment}
          placeholder={t("grants.commentPlaceholder")}
          onChange={(event) => setComment(event.target.value)}
        />
        <button type="button" className="button button--primary" disabled={confirming} onClick={open}>
          {t("grants.submit")}
        </button>
      </div>

      {badInput === "user" ? <Note kind="error">{t("grants.badUser")}</Note> : null}
      {badInput === "tokens" ? <Note kind="error">{t("grants.badTokens", { max: GRANT_MAX_TOKENS })}</Note> : null}

      {grants.outcome ? (
        <Note kind={OUTCOME_KINDS[grants.outcome]}>
          {t(OUTCOME_MESSAGES[grants.outcome], { tokens: grants.result?.tokens ?? 0 })}
        </Note>
      ) : null}

      {confirming ? (
        <GrantConfirm
          userId={parsedUser}
          tokens={parsedTokens}
          comment={comment}
          granting={grants.granting}
          error={grants.error}
          onConfirm={() => void confirm()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </section>
  );
}
